import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TableSubmittableVisits from './components/TableSubmittableVisits';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class HhaExchangeVisitsPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      companies: [],
      caregiverNames: [],
      patientNames: [],

      searching: false,
      submitting: false,
      claimAction: '',

      tableKey: null,
      scheduleVisits: null,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Search SWH/HF Visits to Submit';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/caregiver/get_id_names');
    const promise2 = this.callServiceForObjects('/patient/get_id_names');
    const promise3 = this.callServiceForObjects('/company/get_id_names');

    const results = await Promise.all([promise1, promise2, promise3]);

    // Only these companies need to submit with HHAExchange.
    const companies = results[2].filter(
      c => c.name === 'HomeFirst MLTC' || c.name === 'SWHNY-MLTC' || c.name === 'HealthFirst' ||
        c.name === 'CL HealthCare Inc' || c.name === 'Senior Health Partners');

    this.setState({
      caregiverNames: results[0],
      patientNames: results[1],
      companies,
    });
  }

  async searchVisits(values) {
    try {
      this.setState({
        searching: true,
      });

      const params = {
        id: helpers.extraFormInputValue(values, 'id'),
        companyId: helpers.extraFormSelectValue(values, 'companyId'),
        caregiverId: helpers.extraFormSelectValue(values, 'caregiverId'),
        patientId: helpers.extraFormSelectValue(values, 'patientId'),
        fromDate: helpers.extraFormDateValue(values, 'fromDate'),
        toDate: helpers.extraFormDateValue(values, 'toDate'),
        hhaExchangeStatus: helpers.extraFormSelectValue(values, 'hhaExchangeStatus'),
      };

      const scheduleVisits =
          await this.callServiceForRemoteObjects('/hha_exchange/get_visits', params);

      // Choose the correct claim action.
      const claimAction = {
        UNSUBMIT: 'N',
        FAILURE: 'N',
        SUBMITTED: 'C',
      }[params.hhaExchangeStatus];

      this.setState({
        claimAction,

        tableKey: `${Date.now()}_${Math.random()}`,
        scheduleVisits,
      });
    } finally {
      this.setState({
        searching: false,
      });
    }
  }

  async submitVisits(visitIds, claimAction) {
    try {
      this.setState({
        submitting: true,
      });

      // Submit.
      const params = {
        visitIds,
        claimAction,
      };
      await this.callService('/hha_exchange/submit_visits', params);

      this.notifySuccess('Success', `${visitIds.length} visits have been submit to HHAExchange.`);

      // Re-search.
      this.handleSubmit();
    } finally {
      this.setState({
        submitting: false,
      });
    }
  }

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.searchVisits(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      companies,
      caregiverNames,
      patientNames,
      searching,
      submitting,
      claimAction,
      tableKey,
      scheduleVisits,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="purple_wisteria"
          title="Search SWH/HF Visits to Submit"
          icon="cload-upload"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyId"
                    label="Company"
                    placeholder="Select..."
                    options={companies}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="fromDate"
                    label="From Date"
                    placeholder="mm/dd/yyyy"
                    extra="Search visits from this date"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="toDate"
                    label="To Date"
                    placeholder="mm/dd/yyyy"
                    extra="Search visits to this date"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverId"
                    label="Caregiver Name"
                    placeholder="Select..."
                    options={caregiverNames}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect2
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="patientId"
                    label="Patient Name"
                    placeholder="Select..."
                    options={patientNames}
                    showIdAfterName
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="id"
                    label="Visit Id"
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="hhaExchangeStatus"
                    label="HHAExchange Submit Status"
                    placeholder="Select..."
                    initialValue="UNSUBMIT"
                    allowClear={false}
                    options={['UNSUBMIT', 'SUBMITTED', 'FAILURE']}
                    isRequired
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="purple_wisteria"
                fontSize="14px"
                type="primary"
                loading={searching}
                htmlType="submit"
              >
                SEARCH
                <Icon type="search" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {scheduleVisits &&
          <TableSubmittableVisits
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Visits"
            scheduleVisits={scheduleVisits}
            provider="hhaExchange"
            submitting={submitting}
            claimAction={claimAction}
            submitVisits={this.submitVisits}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
HhaExchangeVisitsPage.propTypes = propTypes;

export default Form.create()(HhaExchangeVisitsPage);
